import { FC, useEffect, useRef, useState } from "react"
import { handleAuthorizedRequest, trends } from "../../../../apis"
import { KDropdown, KSpan } from "kahuna-base-react-components"
import { mapToCamelCase, processPlatformOptions, toYYYYMMDD } from "../../../../utility"
import { AdminTrack, User } from "../../../../types"
//@ts-ignore
import EarthIcon from "../../../../assets/country-icons/earth.svg"
import { getCountryCodeMap, lang } from "../../../../constants/languages"
import { RootState } from "../../../../store"
import { connect } from "react-redux"
import { ANALYSIS_TAB_OPTIONS, countryIcons, DAY, TREND_TRACK_EVENT_TYPE } from "../../../../constants"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRangeDate/KSelectRangeDate"
import { countryMapEn } from "../../../../constants/languages/countryMap/en"
import TrackPerformanceTable from "./TrackPerformanceTable"
import { PlatformPerformanceDetails } from "../platform-performance/PlatformPerformance"
import { eventTypeOptions } from "../artist-overview/ArtistOverview"
import Loading from "../../../ui/Loading"

export interface TrackPerformanceDetails {
  selectedPeriod: PlatformPerformanceDetails
  previousPeriod: PlatformPerformanceDetails
  isrc: string
  track?: AdminTrack
}

export interface TrackPerformanceData {
  options: { platformOptions: string[]; countryOptions: string[] }
  trendTrackPerformanceList: TrackPerformanceDetails[]
  count: number
}

export type TrackPerformanceProps = {
  user: User
  dateValues: DateRangeType
  selectedTab: string
}

export interface TrackPerformanceTableRow {
  isrc: string
  totalStreams: number
  totalSaves: number
  totalSkips: number
  prevPeriodTotalStreams: number
  track: AdminTrack
}

const TrackPerformance: FC<TrackPerformanceProps> = (props) => {
  const {dateValues} = props
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
  const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const [platformOptions, setPlatformOptions] = useState<KSelectOption[]>([])
  const [countryOptions, setCountryOptions] = useState<KSelectOption[]>([])
  const [trendTrackPerformanceList, setTrendTrackPerformanceList] = useState<TrackPerformanceDetails[]>([])
  //const [details, setDetails] = useState<TrackPerformanceDetails>({streams: 0, skips: 0, saves: 0})
  const [pageNumber, setPageNumber] = useState<number>(1)
  const limit = 10
  const [count, setCount] = useState<number>(0)
  const [selectedEventType, setSelectedEventType] = useState<string>(TREND_TRACK_EVENT_TYPE.DSP)
  const [loading, setLoading] = useState(false)
  const lastFetchedDateKeyRef = useRef<string>("")
  const lastRequestKeyRef = useRef("")

  const fetchTrackPerformance = async (eventType: string, startDate: Date, endDate: Date, pageNumber: number) => {
    const start_date = toYYYYMMDD(startDate)
    const end_date = toYYYYMMDD(endDate)
    let fetch_options = false
    const optionsKey = `${start_date}_${end_date}_${eventType}`
    if (lastFetchedDateKeyRef.current !== undefined && lastFetchedDateKeyRef.current !== optionsKey) {
      fetch_options = true
      lastFetchedDateKeyRef.current = optionsKey
    }
    const fetchOptionsParam = fetch_options ? "&fetch_options=1" : ""
    const countryParam = selectedCountries.length > 0 ? `&country_list=${selectedCountries.join(",")}` : ""
    const platformParam = selectedPlatforms.length > 0 ? `&platform_list=${selectedPlatforms.join(",")}` : ""
    const eventTypeParam =
      eventType === "social"
        ? "&event_type=social_production,social_consumption"
        : eventType === "all"
        ? ""
        : `&event_type=${eventType}`

    const offset = (pageNumber - 1) * limit

    setLoading(true)
    try {
      await handleAuthorizedRequest(async () => {
        const response = await trends(
          `/track_performance/?start_date=${start_date}&end_date=${end_date}${countryParam}${platformParam}${fetchOptionsParam}${eventTypeParam}&limit=${limit}&offset=${offset}`,
          "get"
        )
        if (response && response.data) {
          const trackPerformanceData: TrackPerformanceData = mapToCamelCase(response.data)
          const trendTrackData = trackPerformanceData?.trendTrackPerformanceList || []
          setTrendTrackPerformanceList(trendTrackData)
          setCount(trackPerformanceData.count || 0)
          if (fetch_options) {
            const platforms = (trackPerformanceData?.options?.platformOptions || [])?.sort((a, b) => a.localeCompare(b))
            const countries = (trackPerformanceData?.options?.countryOptions || [])?.sort((a, b) => a.localeCompare(b))
            const processedPlatformOptions = processPlatformOptions(platforms)
            setPlatformOptions(processedPlatformOptions)
            const countryCodeMap = getCountryCodeMap()
            const processedCountryOptions = countries.map((country: string, index: number) => {
              const countryKey = country as keyof typeof countryCodeMap
              const label = countryCodeMap[countryKey] ? `${countryCodeMap[countryKey]} (${country})` : country
              const countryMap = countryMapEn
              const countryName = countryMap[countryKey]
              const icon = countryIcons[countryName as keyof typeof countryIcons]

              return { label, value: index, value2: country, icon: icon ? icon : EarthIcon }
            })
            setCountryOptions(processedCountryOptions)
          }
        }
      }, "")
    } finally {
      setLoading(false)
    }
  }

  const makeKey = (dates: Date[], countries: string[], platforms: string[], eventType: string, pageNumber: number) => {
    const start = toYYYYMMDD(dates[0])
    const end = toYYYYMMDD(dates[1])

    const cKey = [...countries].sort().join("|")
    const pKey = [...platforms].sort().join("|")

    return `${start}_${end}__c:${cKey}__p:${pKey}__et:${eventType}__pn:${pageNumber}`
  }

  useEffect(() => {
    if (props.selectedTab !== ANALYSIS_TAB_OPTIONS.TRACK_PERFORMANCE) return 
    const dates = dateValues as Date[]
    if (!(dates[0] && dates[1])) return

    const key = makeKey(dates, selectedCountries, selectedPlatforms, selectedEventType, pageNumber)

    if (key === lastRequestKeyRef.current) return

    const currentBaseKey = key.split("__pn:")[0]
    const lastBaseKey = lastRequestKeyRef.current ? lastRequestKeyRef.current.split("__pn:")[0] : ""

    if (currentBaseKey !== lastBaseKey) {
      if (pageNumber !== 1) {
        setPageNumber(1)
        return
      }
    }

    lastRequestKeyRef.current = key
    // const start = new Date(dates[0].getTime() - 7 * DAY) // It is necessary to get the previous week data
    
    const startDate = dates[0]
    const endDate = dates[1]
    const range = endDate.getTime() - startDate.getTime()
    const start = new Date(startDate.getTime() - (range + DAY))
    
    fetchTrackPerformance(selectedEventType, start, dates[1], pageNumber)
  }, [dateValues, selectedCountries, selectedPlatforms, pageNumber, selectedEventType, props.selectedTab])

  if (loading) return <Loading />
  
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-row gap-5 my-10 items-center justify-between">
        <div className="flex flex-row items-center gap-3 flex-1 flex-wrap">
          <div className="flex flex-col gap-1 justify-center items-start">
            <KSpan text={lang.trends.filter.category} color="#111" fontWeight={500} />
            <KDropdown
              width={140}
              rightIcon="/analytics_icons/caret-down-new.svg"
              enableIndicator={true}
              options={eventTypeOptions}
              onSelect={(selected) => {
                setSelectedEventType((selected as KSelectOption)?.value2 || "")
                setSelectedPlatforms([])
                setSelectedCountries([])
                setPageNumber(1)
              }}
              selected={eventTypeOptions.find((option) => option.value2 === selectedEventType)}
            />
          </div>
          <div className="flex flex-col gap-1 justify-center items-start">
            <KSpan text={lang.trends.filter.platforms} color="#111" fontWeight={500} />
            <KDropdown
              isClearable={true}
              isMulti={true}
              showOnlyIconsInMulti={true}
              width={200}
              closeMenuOnSelect={false}
              options={platformOptions}
              onSelect={() => {}}
              onBlur={(selected) => {
                if (!selected) return
                const value2List = (selected as KSelectOption[])?.map((entity) => entity.value2 || "") || []
                setSelectedPlatforms(value2List)
              }}
              placeholder={lang.trends.filter.all}
              selected={platformOptions.filter(
                (option) => option?.value2 && selectedPlatforms.includes(option?.value2)
              )}
            />
          </div>
          <div className="flex flex-col gap-1 justify-center items-start">
            <KSpan text={lang.trends.filter.countries} color="#111" fontWeight={500} />
            <KDropdown
              isClearable={true}
              isMulti={true}
              showOnlyIconsInMulti={true}
              width={200}
              closeMenuOnSelect={false}
              options={countryOptions}
              onSelect={() => {}}
              onBlur={(selected) => {
                if (!selected) return
                const value2List = (selected as KSelectOption[])?.map((entity) => entity.value2 || "") || []
                setSelectedCountries(value2List)
              }}
              placeholder={lang.trends.filter.all}
              selected={countryOptions.filter((option) => option?.value2 && selectedCountries.includes(option?.value2))}
              sortSelectedFirst={true}
            />
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <div
          style={{
            borderRadius: "10px",
            paddingBottom: 10,
            border: "1px solid var(--stroke-soft-200, #F3F3F3)",
            background: " var(--bg-white-0, #FFF)",
            boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
          }}
          className="flex min-w-0 basis-0 flex-1 h-full items-center justify-center"
        >
          <TrackPerformanceTable
            trendTrackList={trendTrackPerformanceList}
            count={count}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            limit={limit}
            eventType={selectedEventType}
            selectedCountries={selectedCountries}
          />
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps)(TrackPerformance)
